import React, { useLayoutEffect, useRef } from "react";
import styled from "styled-components";
import { borderRadius } from "../../../design-system/tokens/borders";
import { rawColors } from "../../../design-system/tokens/colors";
import { spacing } from "../../../design-system/tokens/spacing";
import { SoonBadge, SOON_TOOLTIP } from "../SoonBadge";
import { Tooltip } from "../Tooltip";
import type { TooltipPosition } from "../Tooltip";

/** Opacity of a control that is shown but not built yet (the canvas action bar's Soon tools use the same). */
const SOON_OPACITY = 0.45;

export type SoonBadgePlacement = "corner" | "before" | "after";

export interface SoonSlotProps {
  children: React.ReactNode;
  /** Fill the parent's width (a card, a form section) instead of hugging the control. */
  block?: boolean;
  /**
   * Keep the slot reachable by keyboard so its tooltip can be read: the children stay focusable but are
   * `aria-disabled` and every click and key press is blocked. Default false: the children are `inert`.
   */
  focusable?: boolean;
  tooltipPosition?: TooltipPosition;
  /**
   * Where the SoonBadge sits. `corner` (default) overlays the slot's top-right corner. `before` and
   * `after` put it beside the children in a row, with no overlay and no clipping.
   */
  badgePlacement?: SoonBadgePlacement;
  className?: string;
}

const Hover = styled(Tooltip)<{ $block: boolean }>`
  display: ${({ $block }) => ($block ? "block" : "inline-flex")};
  width: ${({ $block }) => ($block ? "100%" : "auto")};
`;

const Slot = styled.span<{ $block: boolean }>`
  position: relative;
  display: ${({ $block }) => ($block ? "block" : "inline-flex")};
  width: ${({ $block }) => ($block ? "100%" : "auto")};
  cursor: not-allowed;
`;

/* The slot's own hover reaches the Tooltip because nothing inside it takes pointer events. */
const Inert = styled.span<{ $block: boolean }>`
  display: ${({ $block }) => ($block ? "block" : "inline-flex")};
  width: ${({ $block }) => ($block ? "100%" : "auto")};
  opacity: ${SOON_OPACITY};
  pointer-events: none;
  user-select: none;
`;

/* Focusable variant: dimmed, clicks blocked in the capture phase; the Slot itself takes focus. */
const Dimmed = styled(Inert)`
  pointer-events: none;
`;

/* Inline badge: a sibling of the children in a row (no absolute overlay). */
const Row = styled.span<{ $block: boolean }>`
  display: ${({ $block }) => ($block ? "flex" : "inline-flex")};
  align-items: center;
  gap: ${spacing[2]};
  width: ${({ $block }) => ($block ? "100%" : "auto")};
`;

const Beside = styled.span`
  display: inline-flex;
  flex: none;
  pointer-events: none;
`;

const Corner = styled.span`
  position: absolute;
  top: -${spacing[1]};
  right: -${spacing[1]};
  pointer-events: none;
  line-height: 1;
  background: ${rawColors.structural.white};
  border-radius: ${borderRadius.sm};
`;

/**
 * A control shown but not built yet: its children are inert (no click, no focus), dimmed, with a
 * SoonBadge on the corner and the SOON_TOOLTIP on hover. Wraps any control, card or section so each
 * "Soon" place looks and behaves the same.
 */
export const SoonSlot: React.FC<SoonSlotProps> = ({
  children,
  block = false,
  focusable = false,
  tooltipPosition = "top",
  badgePlacement = "corner",
  className,
}) => {
  const innerRef = useRef<HTMLSpanElement>(null);
  // `inert` takes the children out of the tab order and the click path in one attribute.
  useLayoutEffect(() => {
    if (!focusable) innerRef.current?.setAttribute("inert", "");
  }, [focusable]);
  const inline = badgePlacement !== "corner";
  const badge = inline ? (
    <Beside>
      <SoonBadge size="sm" />
    </Beside>
  ) : (
    <Corner>
      <SoonBadge />
    </Corner>
  );
  const dimmed = focusable ? (
    <Dimmed $block={block && !inline}>{children}</Dimmed>
  ) : (
    <Inert ref={innerRef} $block={block && !inline}>
      {children}
    </Inert>
  );
  const content = inline ? (
    <Row $block={block}>
      {badgePlacement === "before" ? badge : null}
      {dimmed}
      {badgePlacement === "after" ? badge : null}
    </Row>
  ) : (
    <>
      {dimmed}
      {badge}
    </>
  );
  if (focusable) {
    const block_ = (event: React.SyntheticEvent) => {
      // Tab must still move focus out of the slot, or the keyboard is trapped.
      if ((event as React.KeyboardEvent).key === "Tab") return;
      event.preventDefault();
      event.stopPropagation();
    };
    return (
      <Hover label={SOON_TOOLTIP} position={tooltipPosition} $block={block} className={className}>
        <Slot
          $block={block}
          data-testid="soon-slot"
          aria-disabled="true"
          tabIndex={0}
          onClickCapture={block_}
          onKeyDownCapture={block_}
          onKeyUpCapture={block_}
        >
          {content}
        </Slot>
      </Hover>
    );
  }
  return (
    <Hover label={SOON_TOOLTIP} position={tooltipPosition} $block={block} className={className}>
      <Slot $block={block} data-testid="soon-slot">
        {content}
      </Slot>
    </Hover>
  );
};

export default SoonSlot;
